<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useOfficeOfferStore } from '@stores/useOfficeOfferStore';
import { useModal } from '@composables/useModal';
import SlideIn from '@components/Slidein.vue';
import { trans } from '@elements/translation';
import { eventBus } from '@tools/event-bus';
import OfficeChooserTotal from '@pages/offer-page/office-chooser/components/OfficeChooserTotal.vue';
import OfficeChooserChoice from '@pages/offer-page/office-chooser/components/OfficeChooserChoice.vue';

const preselectedOffers = ref(window.initialAppState.preselected_offers);
const preselectedOffersSplitTeamsizes = ref(window.initialAppState.preselected_offers_split_teamsizes);
const offerHashIds = ref([]);
const errorTeamSizes = ref({});
const lastCheckedHashId = ref('');
const offerTeamSizes = ref([]);
const offerTypes = ref([]);
const lastSelectedOfferUrl = ref('');

const isAuthorOrSameOrga = window.initialAppState.is_author_or_same_orga;

const store = useOfficeOfferStore();
const { setOfficeOffersCombinator, isOpenSpace } = store;
const { sameAddressOffers } = storeToRefs(store);

const { isOpen, closeModal, openModal } = useModal();

const orderedSameAddressOffers = computed(() => [ ...sameAddressOffers.value ]
  .sort((a, b) => a.price - b.price || a.surface - b.surface));

const totalSurface = computed(() => sameAddressOffers.value
  .reduce(
    (total, offer) => (offer.isChecked && !isOpenSpace(offer) ? total + offer.surface : total),
    0,
  ));

const totalPrice = computed(() => sameAddressOffers.value.reduce((total, offer) => {
  if (!offer.isChecked) {
    return total;
  }

  const additionalPrice = isOpenSpace(offer)
    ? (offer.price * (offer.chosenTeamSize || 0))
    : offer.price;

  return total + additionalPrice;
}, 0));

const totalTeamSize = computed(() => sameAddressOffers.value.reduce((total, offer) => {
  if (!offer.isChecked) {
    return total;
  }

  const additionalTeamSize = isOpenSpace(offer)
    ? (offer.chosenTeamSize || 0)
    : offer.capacity;

  return total + additionalTeamSize;
}, 0));

const hasOffers = computed(() => offerHashIds.value.length > 0);

const isFormValid = computed(() => hasOffers.value && Object.keys(errorTeamSizes.value).length === 0);

const initSameAddressOffers = () => {
  sameAddressOffers.value = sameAddressOffers.value.map((offer) => ({
    ...offer,
    chosenTeamSize: Number(preselectedOffersSplitTeamsizes.value[offer.hash_id]) || 0,
    isChecked: preselectedOffers.value.includes(offer.hash_id),
  }));
};

initSameAddressOffers();

const updateURLParameter = (url, param, paramVal) => {
  const urlReturn = new URL(url);
  const params = new URLSearchParams(urlReturn.search);
  const baseUrl = url.split('?')[0];

  if (paramVal) {
    params.set(param, paramVal);
  } else {
    params.delete(param);
  }

  return `${baseUrl}?${params.toString()}`;
};

const updateProposalButtonsDataset = (hashIds) => {
  document.querySelectorAll('[data-offer-proposal-button="on"]').forEach(
    (proposalButton) => {
      // eslint-disable-next-line no-param-reassign
      proposalButton.dataset.hashIds = JSON.stringify(hashIds);
    },
  );
};

const generateURL = () => {
  const hashIds = sameAddressOffers.value
    .filter((offer) => offer.isChecked === true)
    .map((offer) => offer.hash_id);

  const teamSizeList = offerTeamSizes.value.map((offer) => offer.teamSize);

  const teamSize = teamSizeList.reduce((a, b) => a + b, 0);

  const params = {
    selected_offers: hashIds.join('|'),
    'team-size-list': teamSizeList.join('|'),
    'team-size': teamSize,
  };

  const url = Object.entries(params).reduce(
    (updatedUrl, [ param, value ]) => updateURLParameter(updatedUrl, param, value),
    window.location.href,
  );

  updateProposalButtonsDataset(hashIds);

  lastSelectedOfferUrl.value = url;

  window.history.pushState({}, 'annonce', url);
};

const checkForErrors = () => {
  errorTeamSizes.value = {};
  offerHashIds.value.forEach((hashId) => {
    const element = document.getElementById(`offer-teamsize-${hashId}`);
    const currentValue = Number(element.value);
    const minValue = Number(element.attributes.min.value);
    const maxValue = Number(element.attributes.max.value);

    if (currentValue === 0 || currentValue < minValue || currentValue > maxValue) {
      errorTeamSizes.value[hashId] = true;
    }
  });
};

/**
 * Make the OS capacity field visible if below slide-in footer when selecting an OS
 */
const scrollIfNeeded = () => {
  const scrollThreshold = 24; // Scroll margin threshold to make osField fully visible
  const osFieldBoundingRect = document.getElementById(`offer-teamsize-${lastCheckedHashId.value}`)?.getBoundingClientRect();
  const slideInFooterBoundingRect = document.querySelector('.slidein-footer').getBoundingClientRect();

  if (osFieldBoundingRect) {
    const delta = slideInFooterBoundingRect.top - (osFieldBoundingRect.top + osFieldBoundingRect.height + scrollThreshold);
    if (delta < 0) {
      document.querySelector('.slidein-content').scrollBy(0, delta * -1);
    }
  }
};

const computeOffersSelectionSummary = (event = null) => {
  // React to checkbox/offer selection only
  if (event) {
    lastCheckedHashId.value = event.target.id.replace('offer-hash-', '');
    // Make OS capacity field visible once OS is selected
    setTimeout(scrollIfNeeded, 1);
  }

  offerHashIds.value = [];
  offerTeamSizes.value = [];
  offerTypes.value = [];
  lastSelectedOfferUrl.value = '';

  sameAddressOffers.value
    .filter((offer) => offer.isChecked)
    .forEach((offer) => {
      const {
        hash_id: hashId,
        capacity,
        url,
        display_type: displayType,
        display_office_type: displayOfficeType,
      } = offer;

      const type = offer.office_type.name;

      const teamSize = Number(document.getElementById(`offer-teamsize-${hashId}`).value) || 0;

      offerHashIds.value = [ ...offerHashIds.value, hashId ];
      offerTeamSizes.value = [ ...offerTeamSizes.value, { hashId, teamSize } ];
      offerTypes.value = [ ...offerTypes.value, {
        type, capacity, teamSize, displayOfficeType, displayType,
      } ];
      lastSelectedOfferUrl.value = url;
    });

  generateURL();
  checkForErrors();
};

const extractHashIdFromUrl = (url) => {
  const { pathname } = new URL(url);
  const hashIdMatch = pathname.match(/^\/(O-[A-Z0-9]+)/);
  return hashIdMatch ? hashIdMatch[1] : null;
};

const redirectToSingleOffer = () => {
  const currentHashId = extractHashIdFromUrl(window.location.href);

  if (offerHashIds.value.length !== 1 || offerHashIds.value[0] === currentHashId) {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  const teamSize = params.has('team-size') ? `?team-size=${params.get('team-size')}` : '';
  document.location = lastSelectedOfferUrl.value + teamSize;

  return true;
};

const refreshOfferPage = () => {
  const currentHashId = extractHashIdFromUrl(window.location.href);

  if (offerHashIds.value.includes(currentHashId)) {
    return false;
  }

  document.location.reload();
  return true;
};

const onCapacityUpdate = (hashId) => {
  checkForErrors();

  if (!errorTeamSizes.value[hashId]) {
    computeOffersSelectionSummary();
  }
};

/**
 * We've got several forms on offer page that'll create a deal
 * (fast_contact, inquiry, etc)
 * So we're updating those forms with selected hashIds
 */
const setFormValues = () => {
  // Iterate over selector, take dataSource and put it in dataDestination hidden field
  const formValues = [
    { selector: '.contact_hash_ids', dataSource: offerHashIds.value, dataDestination: 'hashIds' },
    { selector: '.contact_split_team_sizes', dataSource: offerTeamSizes.value, dataDestination: 'splitTeamSizes' },
  ];

  formValues.forEach(({ selector, dataSource, dataDestination }) => {
    const elementsList = document.querySelectorAll(selector);
    elementsList.forEach((element) => {
      let hiddensList = '';
      const formName = element.id.replace(`_${dataDestination}`, '');

      dataSource.forEach((dataSourceValue, dataSourceIndex) => {
        let index = dataSourceIndex;
        let value = dataSourceValue;
        // We need to get hashId to reference hidden field
        if (typeof dataSourceValue === 'object') {
          index = dataSourceValue.hashId;
          value = dataSourceValue.teamSize;
        }
        hiddensList += `<input type="hidden" name="${formName}[${dataDestination}][${index}]" value="${value}" />`;
      });
      element.innerHTML = hiddensList; // eslint-disable-line no-param-reassign
    });
  });
};

const emitChanges = () => {
  eventBus.dispatchEvent(new CustomEvent('multioffice-change-configuration', {
    detail: {
      capacity: totalTeamSize.value,
      surface: totalSurface.value,
      price: totalPrice.value,
      offerTypes: offerTypes.value,
    },
  }));
};

const updateStoreOfficeOffersCombinator = () => {
  setOfficeOffersCombinator({
    capacity: totalTeamSize.value,
    surface: totalSurface.value,
    price: totalPrice.value,
    offerTypes: offerTypes.value,
    hashIds: offerHashIds.value,
    teamSizes: offerTeamSizes.value,
    totalTeamSize: totalTeamSize.value,
  });
};

const submit = () => {
  computeOffersSelectionSummary();

  if (redirectToSingleOffer()) {
    return;
  }

  if (refreshOfferPage()) {
    return;
  }

  setFormValues();
  updateStoreOfficeOffersCombinator();
  emitChanges();
  closeModal();
};

onMounted(() => {
  eventBus.addEventListener('multioffice-show-slidein', () => {
    openModal();
  });

  computeOffersSelectionSummary();
  updateStoreOfficeOffersCombinator();
  emitChanges();
});

onBeforeUnmount(() => {
  eventBus.removeEventListener('multioffice-show-slidein', () => {
    openModal();
  });
});
</script>

<template>
  <SlideIn
    :is-opened="isOpen"
    @close="closeModal"
  >
    <template #header>
      <span>{{ trans('multioffice.modal.title') }}</span>
    </template>

    <template #body>
      <div class="office-chooser__choices">
        <OfficeChooserChoice
          v-for="offer in orderedSameAddressOffers"
          :key="offer.hash_id"
          v-model:checked="offer.isChecked"
          v-model:chosen-team-size="offer.chosenTeamSize"
          :offer="offer"
          :has-error="errorTeamSizes[offer.hash_id] && lastCheckedHashId !== offer.hash_id"
          :is-author-or-same-orga="isAuthorOrSameOrga"
          @change="computeOffersSelectionSummary"
          @update-capacity="onCapacityUpdate"
          @blur-capacity="lastCheckedHashId = ''"
        />
      </div>
    </template>

    <template #footer>
      <OfficeChooserTotal
        v-if="hasOffers && totalTeamSize"
        class="summary"
        :team-size="totalTeamSize"
        :total-price="totalPrice"
        :total-offers-count="offerHashIds.length"
      />

      <div class="office-chooser__actions">
        <button
          class="btn btn--secondary"
          @click="closeModal"
        >
          {{ trans('multioffice.modal.cancel') }}
        </button>

        <button
          class="btn btn--primary"
          :disabled="!isFormValid || isAuthorOrSameOrga"
          @click="submit"
        >
          {{ trans('multioffice.modal.validate') }}
        </button>
      </div>
    </template>
  </SlideIn>
</template>
